import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Logo from '../../components/Logo';
import MetricsGrid from './components/MetricsGrid';
import QuickActions from './components/QuickActions';
import ContributionsTable from './components/ContributionsTable';
import UpcomingSchedule from './components/UpcomingSchedule';
import SubscriptionManagement from './components/SubscriptionManagement';
import PaymentApproval from './components/PaymentApproval';
import InvoiceGeneration from './components/InvoiceGeneration';
import MemberChargesList from '../../pages/group-management/components/MemberChargesList';
import SyncStatusIndicator from './components/SyncStatusIndicator';
import LendingOverview from './components/LendingOverview';
import { loanService } from '../../services/loanService';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  MetricCard,
  QuickAction,
  ContributionEntry,
  UpcomingDay } from
'./types';

const AdminDashboard = () => {
  const { user, userProfile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // State for real data
  const [metrics, setMetrics] = useState<MetricCard[]>([]);
  const [contributions, setContributions] = useState<ContributionEntry[]>([]);
  const [groupAdmins, setGroupAdmins] = useState<Array<{
    id: string;
    name: string;
    email: string;
    memberCount: number;
    groupName: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check if user is super_admin (must be before useState that uses it)
  const isSuperAdmin = userProfile?.role === 'super_admin';
  const platformRole = (userProfile?.role || '').toString();
  const groupRoles = userProfile?.group_roles || [];
  const isGroupAdminUser =
    platformRole === 'super_admin' ||
    platformRole === 'group_admin' ||
    platformRole === 'group_creator' ||
    groupRoles.includes('admin') ||
    groupRoles.includes('clerk');
  
  const [activeTab, setActiveTab] = useState<'group' | 'admin' | 'subscription' | 'revenue'>(
    isSuperAdmin ? 'revenue' : 'group'
  );
  
  // Company branding state
  const [companyName, setCompanyName] = useState<string>('Nasuman');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>('/assets/images/ihslogo.png');

  // Listen for tab changes from sidebar
  useEffect(() => {
    const handleTabChange = (event: CustomEvent) => {
      const tab = event.detail.tab;
      if (tab === 'group' || tab === 'admin' || tab === 'subscription' || tab === 'revenue') {
        setActiveTab(tab);
      }
    };

    window.addEventListener('dashboard-tab-change', handleTabChange as EventListener);
    return () => {
      window.removeEventListener('dashboard-tab-change', handleTabChange as EventListener);
    };
  }, []);
  
  // Chart data states
  const [contributionChartData, setContributionChartData] = useState<Array<{
    date: string;
    amount: number;
    count: number;
  }>>([]);
  const [loginChartData, setLoginChartData] = useState<Array<{
    date: string;
    logins: number;
  }>>([]);
  const [paymentChartData, setPaymentChartData] = useState<Array<{
    date: string;
    payments: number;
    amount: number;
  }>>([]);
  const [subscriptionData, setSubscriptionData] = useState<{
    totalPayments: number;
    groupsByPlan: Array<{ plan: string; count: number }>;
  }>({ totalPayments: 0, groupsByPlan: [] });

  // Revenue data state (for super_admin)
  const [revenueData, setRevenueData] = useState<{
    totalRevenue: number;
    totalInvoices: number;
    totalSubscriptions: number;
    revenueByPlan: Array<{ plan: string; amount: number; count: number }>;
    revenueByCreator: Array<{ creatorName: string; amount: number; groupCount: number }>;
    monthlyRevenue: Array<{ month: string; amount: number }>;
  }>({
    totalRevenue: 0,
    totalInvoices: 0,
    totalSubscriptions: 0,
    revenueByPlan: [],
    revenueByCreator: [],
    monthlyRevenue: []
  });

  // Helper function to convert logo URL to public URL
  const convertLogoUrl = (logoUrl: string | null): string | null => {
    if (!logoUrl || logoUrl.trim() === '') return null;
    
    // If it's already a full URL (starts with http/https), use it as is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    // If it's a storage path (contains 'company-logos'), convert to public URL
    else if (logoUrl.includes('company-logos')) {
      // Extract the path after 'company-logos/'
      const pathMatch = logoUrl.match(/company-logos\/(.+)/);
      if (pathMatch) {
        const filePath = pathMatch[1];
        const { data: publicUrlData } = supabase.storage
          .from('company-logos')
          .getPublicUrl(filePath);
        return publicUrlData.publicUrl;
      } else {
        return logoUrl;
      }
    }
    // If it's a relative path starting with /, use it as is
    else if (logoUrl.startsWith('/')) {
      return logoUrl;
    }
    // Otherwise, assume it's a storage path and try to convert
    else {
      const { data: publicUrlData } = supabase.storage
        .from('company-logos')
        .getPublicUrl(logoUrl);
      return publicUrlData.publicUrl;
    }
  };

  // Load company branding from user profile, fallback to group logo
  useEffect(() => {
    const loadCompanyBranding = async () => {
      if (!user?.id) return;
      try {
        console.log('[Dashboard] Loading company branding for user:', user.id);
        
        // First, try to get user's profile branding
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('company_name, company_logo_url')
          .eq('id', user.id)
          .single();

        let logoUrl: string | null = null;
        let companyName: string = 'Nasuman';

        // Priority 1: User's profile logo/name
        if (!profileError && userProfile?.company_logo_url && userProfile.company_logo_url.trim() !== '') {
          logoUrl = userProfile.company_logo_url;
          companyName = userProfile.company_name || 'Nasuman';
          console.log('[Dashboard] Using user profile logo:', logoUrl);
        } 
        // Priority 2: Check user's groups for logo
        else {
          console.log('[Dashboard] No user profile logo, checking groups...');
          
          // Get user's groups
          const { data: groupMemberships, error: groupsError } = await supabase
            .from('group_members')
            .select('group_id, groups!inner(id, name, company_name, company_logo_url, created_by)')
            .eq('user_id', user.id)
            .limit(10);

          if (!groupsError && groupMemberships && groupMemberships.length > 0) {
            // Find first group with a logo
            for (const membership of groupMemberships) {
              const group = membership.groups as any;
              if (group?.company_logo_url && group.company_logo_url.trim() !== '') {
                logoUrl = group.company_logo_url;
                companyName = group.company_name || group.name || 'Nasuman';
                console.log('[Dashboard] Found group logo:', logoUrl, 'from group:', group.name);
                break;
              }
              // If group doesn't have logo, check creator's logo
              else if (group?.created_by) {
                const { data: creatorProfile } = await supabase
                  .from('user_profiles')
                  .select('company_name, company_logo_url')
                  .eq('id', group.created_by)
                  .single();
                
                if (creatorProfile?.company_logo_url && creatorProfile.company_logo_url.trim() !== '') {
                  logoUrl = creatorProfile.company_logo_url;
                  companyName = creatorProfile.company_name || group.name || 'Nasuman';
                  console.log('[Dashboard] Found creator logo:', logoUrl, 'from group:', group.name);
                  break;
                }
              }
            }
          }
        }

        // Convert logo URL to public URL if needed
        const finalLogoUrl = convertLogoUrl(logoUrl);
        
        setCompanyName(companyName);
        setCompanyLogoUrl(finalLogoUrl || '/assets/images/ihslogo.png');
        
        console.log('[Dashboard] Final branding:', {
          companyName,
          logoUrl: finalLogoUrl
        });
      } catch (e) {
        console.error('[Dashboard] Error loading company branding:', e);
      }
    };

    loadCompanyBranding();
  }, [user?.id]);

  // Quick actions - filter out contribution recording for super_admin
  const quickActions: QuickAction[] = [
  {
    id: '1',
    title: 'Add New Member',
    description: 'Invite members to join your group',
    icon: 'UserPlus',
    color: 'var(--color-primary)',
    route: '/group-management'
  },
  // Only show contribution recording for non-super_admin users
  ...(!isSuperAdmin ? [{
    id: '2',
    title: 'Record Contribution',
    description: 'Add new contribution entry',
    icon: 'DollarSign',
    color: 'var(--color-success)',
    route: '/contribution-recording'
  }] : []),
  {
    id: '3',
    title: 'Generate Report',
    description: 'Create detailed financial reports',
    icon: 'FileText',
    color: 'var(--color-accent)',
    route: '/reports'
  },
  {
    id: '4',
    title: 'Advanced Analytics',
    description: 'View comprehensive data visualizations',
    icon: 'BarChart3',
    color: 'var(--color-info)',
    route: '/advanced-analytics'
  },
  {
    id: '5',
    title: 'Financial Literacy',
    description: 'Learn financial management skills',
    icon: 'BookOpen',
    color: 'var(--color-warning)',
    route: '/admin-dashboard'
  },
  {
    id: '6',
    title: 'Loan Management',
    description: 'Approve loans and record repayments',
    icon: 'CreditCard',
    color: 'var(--color-secondary)',
    route: '/loan-management'
  }];


  const [upcomingDays, setUpcomingDays] = useState<UpcomingDay[]>([]);

  // Fetch group admins and their member counts (only for current user's groups)
  const fetchGroupAdmins = async () => {
    try {
      console.log('Fetching group admins...');
      
      // First, get the current user's groups (if not super admin)
      let userGroupIds: string[] = [];
      
      if (user && userProfile?.role !== 'super_admin') {
        // Get groups where user is admin or creator
        const { data: adminGroups } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          // group_members.role is a group-scoped role and does NOT include platform roles like 'group_admin'.
          .in('role', ['admin', 'clerk']);

        // Also check if user is creator of any groups
        const { data: createdGroups } = await supabase
          .from('groups')
          .select('id')
          .eq('created_by', user.id);

        const groupIds = new Set<string>();
        if (adminGroups) {
          adminGroups.forEach(g => groupIds.add(g.group_id));
        }
        if (createdGroups) {
          createdGroups.forEach(g => groupIds.add(g.id));
        }

        userGroupIds = Array.from(groupIds);
        console.log('Current user groups:', userGroupIds);

        if (userGroupIds.length === 0) {
          // No groups found, show empty
          setGroupAdmins([]);
          return;
        }
      }

      // Get users who are admins in group_members (filtered by user's groups if not super admin)
      let groupMemberAdmins: any[] = [];
      let memberAdminError: any = null;

      try {
        let query = supabase
          .from('group_members')
          .select(`
            user_id,
            group_id,
            role,
            user_profiles!inner (
              id,
              full_name,
              email
            ),
            groups!inner (
              id,
              name
            )
          `)
          .in('role', ['admin', 'clerk']);

        // Filter by user's groups if not super admin
        if (userGroupIds.length > 0) {
          query = query.in('group_id', userGroupIds);
        }

        const { data: admins1, error: error1 } = await query;

        if (error1) {
          console.warn('Error with foreign key join, trying without:', error1);
          // Fallback: fetch without foreign key
          let fallbackQuery = supabase
            .from('group_members')
            .select('user_id, group_id, role')
            .in('role', ['admin', 'clerk']);

          if (userGroupIds.length > 0) {
            fallbackQuery = fallbackQuery.in('group_id', userGroupIds);
          }

          const { data: admins2, error: error2 } = await fallbackQuery;

          if (error2) {
            memberAdminError = error2;
          } else {
            groupMemberAdmins = admins2 || [];
          }
        } else {
          groupMemberAdmins = admins1 || [];
        }
      } catch (err) {
        console.error('Error in group member admins fetch:', err);
        memberAdminError = err;
      }

      // Also get group creators who might not be in group_members (filtered by user's groups if not super admin)
      let createdGroups: any[] = [];
      let groupsError: any = null;

      try {
        let groupsQuery = supabase
          .from('groups')
          .select(`
            id,
            name,
            created_by,
            user_profiles!groups_created_by_fkey (
              id,
              full_name,
              email,
              role
            )
          `);

        // Filter by user's groups if not super admin
        if (userGroupIds.length > 0) {
          groupsQuery = groupsQuery.in('id', userGroupIds);
        }

        const { data: groups1, error: error1 } = await groupsQuery;

        if (error1) {
          console.warn('Error with foreign key join for groups, trying without:', error1);
          // Fallback: fetch groups and profiles separately
          let fallbackGroupsQuery = supabase
            .from('groups')
            .select('id, name, created_by');

          if (userGroupIds.length > 0) {
            fallbackGroupsQuery = fallbackGroupsQuery.in('id', userGroupIds);
          }

          const { data: groups2, error: error2 } = await fallbackGroupsQuery;

          if (error2) {
            groupsError = error2;
          } else if (groups2 && groups2.length > 0) {
            const creatorIds = [...new Set(groups2.map((g: any) => g.created_by).filter(Boolean))];
            const { data: creators } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, role')
              .in('id', creatorIds);

            const creatorsMap = new Map(creators?.map((c: any) => [c.id, c]) || []);
            createdGroups = groups2.map((g: any) => ({
              ...g,
              user_profiles: creatorsMap.get(g.created_by)
            }));
          }
        } else {
          createdGroups = groups1 || [];
        }
      } catch (err) {
        console.error('Error fetching created groups:', err);
        groupsError = err;
      }

      // Combine and deduplicate admins
      const adminMap = new Map<string, {
        id: string;
        name: string;
        email: string;
        groupIds: string[];
        groupNames: string[];
      }>();

      // Note: We don't add all adminUsers from user_profiles anymore
      // We only add admins that are associated with the current user's groups

      // Add admins from group_members and track their groups
      if (groupMemberAdmins && groupMemberAdmins.length > 0) {
        console.log('Found admins from group_members:', groupMemberAdmins.length);
        
        // If we got data without foreign keys, fetch profiles and groups separately
        if (!groupMemberAdmins[0].user_profiles) {
          const userIds = [...new Set(groupMemberAdmins.map((gm: any) => gm.user_id))];
          const groupIds = [...new Set(groupMemberAdmins.map((gm: any) => gm.group_id))];
          
          // Fetch user profiles
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, full_name, email')
            .in('id', userIds);
          
          // Fetch groups
          const { data: groups } = await supabase
            .from('groups')
            .select('id, name')
            .in('id', groupIds);
          
          const profilesMap = new Map(profiles?.map((p: any) => [p.id, p]) || []);
          const groupsMap = new Map(groups?.map((g: any) => [g.id, g]) || []);
          
          groupMemberAdmins.forEach((gm: any) => {
            const userId = gm.user_id;
            const profile = profilesMap.get(userId);
            const group = groupsMap.get(gm.group_id);
            
            if (!adminMap.has(userId)) {
              adminMap.set(userId, {
                id: userId,
                name: profile?.full_name || 'Unknown',
                email: profile?.email || '',
                groupIds: [],
                groupNames: []
              });
            }

            const admin = adminMap.get(userId)!;
            if (group && !admin.groupIds.includes(group.id)) {
              admin.groupIds.push(group.id);
              admin.groupNames.push(group.name || 'Unknown Group');
            }
          });
        } else {
          // Data has foreign keys, use them directly
          groupMemberAdmins.forEach((gm: any) => {
            const userId = gm.user_id;
            const profile = gm.user_profiles;
            const group = gm.groups;
            
            if (!adminMap.has(userId)) {
              adminMap.set(userId, {
                id: userId,
                name: profile?.full_name || 'Unknown',
                email: profile?.email || '',
                groupIds: [],
                groupNames: []
              });
            }

            const admin = adminMap.get(userId)!;
            if (group && !admin.groupIds.includes(group.id)) {
              admin.groupIds.push(group.id);
              admin.groupNames.push(group.name || 'Unknown Group');
            }
          });
        }
      }

      // Add group creators who might not be in group_members
      if (createdGroups && createdGroups.length > 0) {
        console.log('Found created groups:', createdGroups.length);
        createdGroups.forEach((group: any) => {
          const creator = group.user_profiles;
          if (creator) {
            const userId = creator.id;
            if (!adminMap.has(userId)) {
              adminMap.set(userId, {
                id: userId,
                name: creator.full_name || 'Unknown',
                email: creator.email || '',
                groupIds: [],
                groupNames: []
              });
            }

            const admin = adminMap.get(userId)!;
            if (group && !admin.groupIds.includes(group.id)) {
              admin.groupIds.push(group.id);
              admin.groupNames.push(group.name || 'Unknown Group');
            }
          }
        });
      }

      console.log('Total unique admins found:', adminMap.size);

      // For each admin, count members in their groups (only from current user's groups if not super admin)
      const adminsWithCounts = await Promise.all(
        Array.from(adminMap.values()).map(async (admin) => {
          let totalMemberCount = 0;

          if (admin.groupIds.length > 0) {
            // Filter groupIds to only include groups from current user's groups (if not super admin)
            let groupsToCount = admin.groupIds;
            if (userGroupIds.length > 0) {
              groupsToCount = admin.groupIds.filter(id => userGroupIds.includes(id));
            }

            if (groupsToCount.length > 0) {
              // Count members in all groups this admin manages (within current user's groups)
              const { count, error: countError } = await supabase
                .from('group_members')
                .select('*', { count: 'exact', head: true })
                .in('group_id', groupsToCount)
                .eq('role', 'member');

              if (!countError) {
                totalMemberCount = count || 0;
              } else {
                console.warn(`Error counting members for admin ${admin.id}:`, countError);
              }
            }
          }

          // Filter group names to only show groups from current user's groups (if not super admin)
          let displayGroupNames = admin.groupNames;
          if (userGroupIds.length > 0 && admin.groupIds.length > 0) {
            // We need to match group names with group IDs
            // For now, just show all group names since we've already filtered the admins
            displayGroupNames = admin.groupNames;
          }

          return {
            id: admin.id,
            name: admin.name,
            email: admin.email,
            memberCount: totalMemberCount,
            groupName: displayGroupNames.join(', ') || 'No groups'
          };
        })
      );

      console.log('Final admins with counts:', adminsWithCounts.length);
      setGroupAdmins(adminsWithCounts);
    } catch (err: any) {
      console.error('Error fetching group admins:', err);
      setGroupAdmins([]); // Set empty array on error
    }
  };

  // Fetch dashboard data
  useEffect(() => {
    if (!user || !userProfile) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Super admin: Can view all contributions (read-only) but cannot create/edit
        if (isSuperAdmin) {
          // Fetch all contributions for superadmin to view
          const { data: allContributions, error: contribError } = await supabase
            .from('contributions')
            .select(`
              id,
              amount,
              contribution_date,
              status,
              notes,
              member:user_profiles!member_id (
                id,
                full_name,
                email
              ),
              recorder:user_profiles!recorded_by (
                id,
                full_name
              ),
              group:groups!group_id (
                id,
                name
              )
            `)
            .order('contribution_date', { ascending: false })
            .limit(50);

          if (!contribError && allContributions) {
            const transformedContributions: ContributionEntry[] = allContributions.map((c: any) => ({
              id: c.id,
              memberName: c.member?.full_name || 'Unknown Member',
              memberAvatar: c.member?.avatar_url || "https://img.rocket.new/generatedImages/default-avatar.png",
              memberAvatarAlt: `Avatar of ${c.member?.full_name || 'member'}`,
              amount: Number(c.amount),
              date: new Date(c.contribution_date),
              recordedBy: c.recorder?.full_name || 'System',
              verificationStatus: c.status === 'approved' ? 'verified' : c.status === 'rejected' ? 'rejected' : 'pending',
              notes: c.notes,
              groupName: c.group?.name || 'Unknown Group'
            }));

            setContributions(transformedContributions);
          }

          // Fetch total unique users in groups (members, admins, clerks)
          // This counts all users who are part of groups, not just platform-level members
          const { data: groupMembersData } = await supabase
            .from('group_members')
            .select('user_id')
            .in('role', ['member', 'admin', 'clerk']);

          // Get unique user count
          const uniqueUserIds = new Set(groupMembersData?.map(gm => gm.user_id) || []);
          const totalUsers = uniqueUserIds.size;

          // Fetch group counts
          const { data: groupsData } = await supabase
            .from('groups')
            .select('id, is_active');

          // Fetch total membership fees across all groups
          const { data: totalFeesData } = await supabase
            .from('membership_fee_payments')
            .select('amount');

          const activeGroups = groupsData?.filter(g => g.is_active).length || 0;
          const totalGroups = groupsData?.length || 0;
          const totalMembershipFees = totalFeesData?.reduce((sum, f) => sum + Number(f.amount), 0) || 0;

          setMetrics([
            {
              id: '1',
              title: 'Total Users',
              value: totalUsers,
              change: 8.2,
              changeType: 'increase',
              icon: 'Users',
              color: 'var(--color-primary)'
            },
            {
              id: '2',
              title: 'Membership Fees',
              value: `${totalMembershipFees.toLocaleString()} VT`,
              change: 0,
              changeType: 'increase',
              icon: 'ShieldCheck',
              color: 'var(--color-success)'
            },
            {
              id: '3',
              title: 'Active Groups',
              value: activeGroups,
              change: 0,
              changeType: 'increase',
              icon: 'Package',
              color: 'var(--color-info)'
            },
            {
              id: '4',
              title: 'Total Groups',
              value: totalGroups,
              change: 0,
              changeType: 'increase',
              icon: 'Layers',
              color: 'var(--color-accent)'
            }
          ]);

          // Fetch all group admins and their member counts
          await fetchGroupAdmins();

          // Fetch chart data for super admin
          await fetchChartData();
          if (isSuperAdmin) {
            await fetchRevenueData();
          }

          setLoading(false);
          return;
        }

        // Get current user's groups first (for filtering)
        let userGroupIds: string[] = [];
        if (user) {
          // Get groups where user is admin or creator
          const { data: adminGroups } = await supabase
            .from('group_members')
            .select('group_id')
            .eq('user_id', user.id)
            .in('role', ['admin', 'clerk']);

          // Also check if user is creator of any groups
          const { data: createdGroups } = await supabase
            .from('groups')
            .select('id')
            .eq('created_by', user.id);

          const groupIds = new Set<string>();
          if (adminGroups) {
            adminGroups.forEach(g => groupIds.add(g.group_id));
          }
          if (createdGroups) {
            createdGroups.forEach(g => groupIds.add(g.id));
          }
          userGroupIds = Array.from(groupIds);
          console.log(`[Active Members] User groups found: ${userGroupIds.length}`, userGroupIds);
        }

        // Regular admin: Fetch all metrics including contributions
        const [
          contributionsData,
          membersData,
          pendingData,
          monthlyData,
          membershipFeesData
        ] = await Promise.all([
          // Total contributions (filtered by user's groups)
          userGroupIds.length > 0
            ? supabase
                .from('contributions')
                .select('amount')
                .eq('status', 'approved')
                .in('group_id', userGroupIds)
            : supabase
                .from('contributions')
                .select('amount')
                .eq('status', 'approved'),
          
          // Active members count (only in user's groups) - count all unique members (matching subscription management)
          (async () => {
            if (userGroupIds.length > 0) {
              // Get all group members (matching subscription management approach - no role filter)
              const { data: membersData, error: membersError } = await supabase
                .from('group_members')
                .select('user_id')
                .in('group_id', userGroupIds);
              
              if (membersError) {
                console.error('Error counting members:', membersError);
                return { count: 0, error: membersError };
              }
              
              // Count unique members (a user might be in multiple groups)
              const uniqueMemberIds = new Set((membersData || []).map((m: any) => m.user_id));
              console.log(`[Active Members] Found ${uniqueMemberIds.size} unique members in ${userGroupIds.length} groups (total rows: ${membersData?.length || 0})`);
              return { count: uniqueMemberIds.size, error: null };
            } else {
              // Get all group members
              const { data: membersData, error: membersError } = await supabase
                .from('group_members')
                .select('user_id');
              
              if (membersError) {
                console.error('Error counting members:', membersError);
                return { count: 0, error: membersError };
              }
              
              // Count unique members
              const uniqueMemberIds = new Set((membersData || []).map((m: any) => m.user_id));
              return { count: uniqueMemberIds.size, error: null };
            }
          })(),
          
          // Pending verifications (filtered by user's groups)
          userGroupIds.length > 0
            ? supabase
                .from('contributions')
                .select('id')
                .eq('status', 'pending_approval')
                .in('group_id', userGroupIds)
            : supabase
                .from('contributions')
                .select('id')
                .eq('status', 'pending_approval'),
          
          // This month's contributions (filtered by user's groups)
          userGroupIds.length > 0
            ? supabase
                .from('contributions')
                .select('amount')
                .eq('status', 'approved')
                .in('group_id', userGroupIds)
                .gte('contribution_date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString())
            : supabase
                .from('contributions')
                .select('amount')
                .eq('status', 'approved')
                .gte('contribution_date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()),

          // Membership fees (filtered by user's groups)
          userGroupIds.length > 0
            ? supabase
                .from('membership_fee_payments')
                .select('amount')
                .in('group_id', userGroupIds)
            : supabase
                .from('membership_fee_payments')
                .select('amount')
        ]);

        // Calculate metrics
        const totalContributions = contributionsData.data?.reduce((sum, c) => sum + Number(c.amount), 0) || 0;
        const monthlyTotal = monthlyData.data?.reduce((sum, c) => sum + Number(c.amount), 0) || 0;
        const totalMembershipFees = membershipFeesData.data?.reduce((sum, f) => sum + Number(f.amount), 0) || 0;

        // Cash in hand (admin-only): sum across user's groups
        let cashInHandTotal = 0;
        if (isGroupAdminUser && userGroupIds.length > 0) {
          try {
            const cashValues = await Promise.all(
              userGroupIds.map((gid) => loanService.getGroupCashInHand(gid))
            );
            cashInHandTotal = cashValues.reduce((sum, v) => sum + Number(v || 0), 0);
          } catch (e) {
            console.warn('Failed to load cash in hand:', e);
          }
        }

        const nextMetrics = [
          {
            id: '1',
            title: 'Total Contributions',
            value: `${totalContributions.toLocaleString()} VT`,
            change: 12.5,
            changeType: 'increase',
            icon: 'DollarSign',
            color: 'var(--color-success)'
          },
          {
            id: '2',
            title: 'Membership Fees',
            value: `${totalMembershipFees.toLocaleString()} VT`,
            change: 0,
            changeType: 'increase',
            icon: 'ShieldCheck',
            color: 'var(--color-primary)'
          },
          {
            id: '3',
            title: 'Active Members',
            value: typeof membersData === 'object' && 'count' in membersData ? membersData.count : 0,
            change: 8.2,
            changeType: 'increase',
            icon: 'Users',
            color: 'var(--color-info)'
          },
          {
            id: '4',
            title: 'Pending Verifications',
            value: pendingData.data?.length || 0,
            change: 5.1,
            changeType: 'decrease',
            icon: 'Clock',
            color: 'var(--color-warning)'
          }
        ];

        if (isGroupAdminUser) {
          nextMetrics.unshift({
            id: 'cash',
            title: 'Cash in Hand',
            value: `${Math.round(cashInHandTotal).toLocaleString()} VT`,
            change: 0,
            changeType: 'increase',
            icon: 'Wallet',
            color: 'var(--color-success)'
          });
        }

        setMetrics(nextMetrics);

        // Fetch recent contributions with member details
        const { data: recentContributions, error: contribError } = await supabase
          .from('contributions')
          .select(`
            id,
            amount,
            contribution_date,
            status,
            notes,
            member:user_profiles!member_id (
              id,
              full_name,
              email,
              avatar_url
            ),
            recorder:user_profiles!recorded_by (
              id,
              full_name
            )
          `)
          .order('contribution_date', { ascending: false })
          .limit(10);

        if (contribError) throw contribError;

        // Transform contributions data
        const transformedContributions: ContributionEntry[] = recentContributions?.map(c => {
          const member = c.member as any; // Member info from foreign key
          const recorder = c.recorder as any; // Recorder info from foreign key
          
          return {
            id: c.id,
            memberName: member?.full_name || 'Unknown Member',
            memberAvatar: member?.avatar_url || "https://img.rocket.new/generatedImages/default-avatar.png",
            memberAvatarAlt: `Avatar of ${member?.full_name || 'member'}`,
            amount: Number(c.amount),
            date: new Date(c.contribution_date),
            recordedBy: recorder?.full_name || 'System',
            verificationStatus: c.status === 'approved' ? 'verified' : c.status === 'rejected' ? 'rejected' : 'pending',
            notes: c.notes
          };
        }) || [];

        setContributions(transformedContributions);

        // Calculate upcoming collection days (assuming weekly on Sundays)
        // This can be enhanced to fetch from a contribution schedule table if it exists
        const today = new Date();
        const upcoming: UpcomingDay[] = [];
        const totalMembers = membersData.data?.length || 0;
        
        // Generate next 3 Sundays
        for (let i = 0; i < 3; i++) {
          const nextSunday = new Date(today);
          const daysUntilSunday = (7 - today.getDay()) % 7 || 7;
          nextSunday.setDate(today.getDate() + daysUntilSunday + (i * 7));
          
          // Calculate participation for this day (if it's in the past, use historical data)
          const dayStart = new Date(nextSunday);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(nextSunday);
          dayEnd.setHours(23, 59, 59, 999);
          
          const { data: dayContribs } = await supabase
            .from('contributions')
            .select('member_id')
            .gte('contribution_date', dayStart.toISOString())
            .lte('contribution_date', dayEnd.toISOString());
          
          const contributedMembers = new Set(dayContribs?.map(c => c.member_id) || []).size;
          const participationRate = totalMembers > 0 ? (contributedMembers / totalMembers) * 100 : 0;
          
          upcoming.push({
            id: `upcoming-${i}`,
            date: nextSunday,
            dayName: `${nextSunday.toLocaleDateString('en-US', { weekday: 'long' })} Collection`,
            participationRate: Math.round(participationRate),
            totalMembers,
            contributedMembers,
            reminderSent: false
          });
        }
        
        setUpcomingDays(upcoming);

        // Fetch group admins for all users
        await fetchGroupAdmins();

        // Fetch chart data
        await fetchChartData();

      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, userProfile, isSuperAdmin]);

  // Fetch chart data for all tabs
  const fetchChartData = async () => {
    try {
      // Get user's group IDs if not super admin (get all groups, not just one)
      let userGroupIds: string[] = [];
      if (!isSuperAdmin && user) {
        // Get groups where user is admin or creator
        const { data: adminGroups } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          .in('role', ['admin', 'clerk']);

        // Also check if user is creator of any groups
        const { data: createdGroups } = await supabase
          .from('groups')
          .select('id')
          .eq('created_by', user.id);

        const groupIds = new Set<string>();
        if (adminGroups) {
          adminGroups.forEach(g => groupIds.add(g.group_id));
        }
        if (createdGroups) {
          createdGroups.forEach(g => groupIds.add(g.id));
        }
        userGroupIds = Array.from(groupIds);
        console.log(`[Contribution Trends] User groups: ${userGroupIds.length}`, userGroupIds);
      }

      // Fetch contribution trends data (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      let contributionQuery = supabase
        .from('contributions')
        .select('amount, contribution_date, status')
        .gte('contribution_date', thirtyDaysAgo.toISOString())
        .eq('status', 'approved');
      
      if (userGroupIds.length > 0 && !isSuperAdmin) {
        contributionQuery = contributionQuery.in('group_id', userGroupIds);
      }

      const { data: contributionsData } = await contributionQuery;

      // Group contributions by date
      const contributionsByDate = new Map<string, { amount: number; count: number }>();
      
      contributionsData?.forEach((contrib: any) => {
        const date = new Date(contrib.contribution_date).toISOString().split('T')[0];
        const existing = contributionsByDate.get(date) || { amount: 0, count: 0 };
        contributionsByDate.set(date, {
          amount: existing.amount + Number(contrib.amount),
          count: existing.count + 1,
        });
      });

      // Convert to array and sort by date
      const contributionChart = Array.from(contributionsByDate.entries())
        .map(([date, data]) => ({
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          dateValue: date, // Keep original date for sorting
          amount: data.amount,
          count: data.count,
        }))
        .sort((a, b) => a.dateValue.localeCompare(b.dateValue))
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting

      setContributionChartData(contributionChart);

      // Login activity: avoid querying non-existent `login_history` table (causes 404 + slows load).
      // We already have audit_logs / RPC-based login counts elsewhere in the app.
      // For now, keep this chart empty until we migrate it to audit_logs.
      setLoginChartData([]);

      // Fetch payment activity data (last 30 days)
      let paymentQuery = supabase
        .from('payment_transactions')
        .select('amount_vatu, created_at, payment_status')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .eq('payment_status', 'approved');
      
      if (userGroupIds.length > 0 && !isSuperAdmin) {
        paymentQuery = paymentQuery.in('group_id', userGroupIds);
      }

      const { data: paymentsData } = await paymentQuery;

      // Group payments by date
      const paymentsByDate = new Map<string, { payments: number; amount: number }>();
      
      paymentsData?.forEach((payment: any) => {
        const date = new Date(payment.created_at).toISOString().split('T')[0];
        const existing = paymentsByDate.get(date) || { payments: 0, amount: 0 };
        paymentsByDate.set(date, {
          payments: existing.payments + 1,
          amount: existing.amount + Number(payment.amount_vatu || 0),
        });
      });

      const paymentChart = Array.from(paymentsByDate.entries())
        .map(([date, data]) => ({
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          dateValue: date, // Keep original date for sorting
          payments: data.payments,
          amount: data.amount,
        }))
        .sort((a, b) => a.dateValue.localeCompare(b.dateValue))
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting

      setPaymentChartData(paymentChart);

      // Fetch subscription data
      let subscriptionQuery = supabase
        .from('user_subscriptions')
        .select(`
          plan:subscription_plans!plan_id (
            plan_name
          )
        `);
      
      if (userGroupIds.length > 0 && !isSuperAdmin) {
        // Get group members from all user groups
        const { data: groupMembers } = await supabase
          .from('group_members')
          .select('user_id')
          .in('group_id', userGroupIds);
        
        if (groupMembers && groupMembers.length > 0) {
          const uniqueUserIds = [...new Set(groupMembers.map(m => m.user_id))];
          subscriptionQuery = subscriptionQuery.in('user_id', uniqueUserIds);
        }
      }

      const { data: subscriptionsData } = await subscriptionQuery;

      // Calculate total payments
      let totalPaymentsQuery = supabase
        .from('payment_transactions')
        .select('amount_vatu')
        .eq('payment_status', 'approved');
      
      if (userGroupIds.length > 0 && !isSuperAdmin) {
        totalPaymentsQuery = totalPaymentsQuery.in('group_id', userGroupIds);
      }

      const { data: allPayments } = await totalPaymentsQuery;
      const totalPayments = allPayments?.reduce((sum, p) => sum + Number(p.amount_vatu || 0), 0) || 0;

      // Group subscriptions by plan
      const groupsByPlan = new Map<string, number>();
      subscriptionsData?.forEach((sub: any) => {
        const planName = sub.plan?.plan_name || 'Unknown';
        groupsByPlan.set(planName, (groupsByPlan.get(planName) || 0) + 1);
      });

      setSubscriptionData({
        totalPayments,
        groupsByPlan: Array.from(groupsByPlan.entries()).map(([plan, count]) => ({
          plan,
          count,
        })),
      });

    } catch (err: any) {
      console.error('Error fetching chart data:', err);
    }
  };

  // Fetch platform revenue data (super_admin only)
  const fetchRevenueData = async () => {
    if (!isSuperAdmin) return;

    try {
      // 1. Total revenue from all approved payments
      const { data: allPayments } = await supabase
        .from('payment_transactions')
        .select('amount_vatu, created_at, user_id, subscription_id')
        .eq('payment_status', 'approved');

      const totalRevenue = allPayments?.reduce((sum, p) => sum + Number(p.amount_vatu || 0), 0) || 0;

      // 2. Total revenue from invoices
      const { data: allInvoices } = await supabase
        .from('invoice_generation_requests')
        .select('total_amount, created_at, status')
        .eq('status', 'generated');

      const totalInvoices = allInvoices?.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0) || 0;

      // 3. Revenue by subscription plan
      const { data: subscriptionsWithPlans } = await supabase
        .from('user_subscriptions')
        .select(`
          plan:subscription_plans!plan_id (
            id,
            plan_name,
            plan_type,
            price_vatu
          )
        `);

      const revenueByPlanMap = new Map<string, { amount: number; count: number }>();
      subscriptionsWithPlans?.forEach((sub: any) => {
        const planName = sub.plan?.plan_name || 'Unknown';
        const planPrice = Number(sub.plan?.price_vatu || 0);
        const existing = revenueByPlanMap.get(planName) || { amount: 0, count: 0 };
        revenueByPlanMap.set(planName, {
          amount: existing.amount + planPrice,
          count: existing.count + 1,
        });
      });

      // Also add revenue from payments linked to subscriptions (optimized - bulk fetch)
      const paymentSubscriptionIds = allPayments?.filter(p => p.subscription_id).map(p => p.subscription_id) || [];
      if (paymentSubscriptionIds.length > 0) {
        const uniqueSubscriptionIds = [...new Set(paymentSubscriptionIds)];
        const { data: subscriptionsForPayments } = await supabase
          .from('user_subscriptions')
          .select('id, plan:subscription_plans!plan_id(plan_name)')
          .in('id', uniqueSubscriptionIds);

        // Create a map of subscription_id to plan_name
        const subscriptionToPlanMap = new Map<string, string>();
        subscriptionsForPayments?.forEach((sub: any) => {
          if (sub.plan?.plan_name) {
            subscriptionToPlanMap.set(sub.id, sub.plan.plan_name);
          }
        });

        // Add payment amounts to plan revenue
        allPayments?.forEach((payment: any) => {
          if (payment.subscription_id) {
            const planName = subscriptionToPlanMap.get(payment.subscription_id);
            if (planName) {
              const existing = revenueByPlanMap.get(planName) || { amount: 0, count: 0 };
              revenueByPlanMap.set(planName, {
                amount: existing.amount + Number(payment.amount_vatu || 0),
                count: existing.count,
              });
            }
          }
        });
      }

      const revenueByPlan = Array.from(revenueByPlanMap.entries()).map(([plan, data]) => ({
        plan,
        amount: data.amount,
        count: data.count,
      }));

      // 4. Revenue by group creator (optimized - get all data in bulk)
      let revenueByCreator: Array<{ creatorName: string; amount: number; groupCount: number }> = [];
      
      const { data: allGroups } = await supabase
        .from('groups')
        .select('id, name, created_by, created_at')
        .eq('is_active', true);

      if (allGroups && allGroups.length > 0) {
        const creatorIds = [...new Set(allGroups.map(g => g.created_by).filter(Boolean))];
        const { data: creators } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', creatorIds);

        // Get subscription payments made by group creators (not by group members)
        // Revenue should only count subscription payments, not member addition charges
        const { data: creatorPayments } = await supabase
          .from('payment_transactions')
          .select('amount_vatu, user_id, payment_type')
          .eq('payment_status', 'approved')
          .in('user_id', creatorIds)
          .or('payment_type.is.null,payment_type.eq.subscription'); // Only subscription payments, exclude member_addition

        // Calculate revenue by creator
        const revenueByCreatorMap = new Map<string, { amount: number; groupCount: number }>();
        const paymentsByCreatorId = new Map<string, number>();
        
        // Sum up subscription payments for each creator (total across all their payments)
        creatorPayments?.forEach((payment: any) => {
          const creatorId = payment.user_id;
          const amount = Number(payment.amount_vatu || 0);
          paymentsByCreatorId.set(creatorId, (paymentsByCreatorId.get(creatorId) || 0) + amount);
        });

        // Map groups to creators and calculate their total subscription revenue
        // Note: Revenue is per creator (not per group), so we sum once per creator
        allGroups.forEach((group) => {
          const creator = creators?.find(c => c.id === group.created_by);
          if (creator) {
            const creatorName = creator.full_name || creator.email || 'Unknown';
            const existing = revenueByCreatorMap.get(creatorName);
            
            // If this creator hasn't been processed yet, add their revenue
            if (!existing) {
              const creatorRevenue = paymentsByCreatorId.get(creator.id) || 0;
              revenueByCreatorMap.set(creatorName, {
                amount: creatorRevenue,
                groupCount: 1,
              });
            } else {
              // Creator already processed, just increment group count
              revenueByCreatorMap.set(creatorName, {
                amount: existing.amount, // Keep the same revenue (don't add again)
                groupCount: existing.groupCount + 1,
              });
            }
          }
        });

        revenueByCreator = Array.from(revenueByCreatorMap.entries())
          .map(([creatorName, data]) => ({
            creatorName,
            amount: data.amount,
            groupCount: data.groupCount,
          }))
          .sort((a, b) => b.amount - a.amount);
      }

      // 5. Monthly revenue trends (last 12 months)
      const monthlyRevenueMap = new Map<string, number>();
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

      allPayments?.forEach((payment: any) => {
        const paymentDate = new Date(payment.created_at);
        if (paymentDate >= twelveMonthsAgo) {
          const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
          const existing = monthlyRevenueMap.get(monthKey) || 0;
          monthlyRevenueMap.set(monthKey, existing + Number(payment.amount_vatu || 0));
        }
      });

      const monthlyRevenue = Array.from(monthlyRevenueMap.entries())
        .map(([month, amount]) => ({
          month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          amount,
        }))
        .sort((a, b) => a.month.localeCompare(b.month));

      setRevenueData({
        totalRevenue: totalRevenue + totalInvoices,
        totalInvoices,
        totalSubscriptions: totalRevenue,
        revenueByPlan,
        revenueByCreator,
        monthlyRevenue,
      });
    } catch (err: any) {
      console.error('Error fetching revenue data:', err);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contributions')
        .update({ status: 'approved' })
        .eq('id', id);

      if (error) throw error;

      // Refresh contributions
      setContributions(prev => 
        prev.map(c => 
          c.id === id ? { ...c, verificationStatus: 'verified' } : c
        )
      );
    } catch (err: any) {
      console.error('Error approving contribution:', err);
      alert('Failed to approve contribution');
    }
  };

  const handleReject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contributions')
        .update({ status: 'rejected' })
        .eq('id', id);

      if (error) throw error;

      // Refresh contributions
      setContributions(prev => 
        prev.map(c => 
          c.id === id ? { ...c, verificationStatus: 'rejected' } : c
        )
      );
    } catch (err: any) {
      console.error('Error rejecting contribution:', err);
      alert('Failed to reject contribution');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-destructive mb-4">Error loading dashboard: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{companyName} - Admin Dashboard</title>
        <meta
          name="description"
          content="Comprehensive admin dashboard for managing community group contributions, members, and financial tracking" />

      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen} />


        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar 
              isCollapsed={isSidebarCollapsed} 
              userRole="admin" 
              isSuperAdmin={isSuperAdmin}
              activeDashboardTab={activeTab}
            />
          </div>

          <main
            className={`flex-1 transition-layout ${
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`
            }>

            <div className="p-4 lg:p-6 max-w-7xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              {/* Welcome Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  {companyLogoUrl ? (
                    <img
                      src={companyLogoUrl}
                      alt={companyName}
                      className="w-10 h-10 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/assets/images/ihslogo.png';
                      }}
                    />
                  ) : (
                    <Logo size={40} color="gradient" variant="icon" />
                  )}
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                      {companyName}
                    </h1>
                    <p className="text-slate-600 mt-1">
                      Welcome back! Here's an overview of your community group activities.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="mb-8">
                <div className="inline-flex gap-2 p-1 bg-white rounded-xl shadow-soft border border-gray-100">
                  {!isSuperAdmin && (
                    <>
                      <button
                        onClick={() => setActiveTab('group')}
                        className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 ${
                          activeTab === 'group'
                            ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        Group
                      </button>
                      <button
                        onClick={() => setActiveTab('admin')}
                        className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 ${
                          activeTab === 'admin'
                            ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        Group Admin
                      </button>
                      <button
                        onClick={() => setActiveTab('subscription')}
                        className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 ${
                          activeTab === 'subscription'
                            ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        Subscription
                      </button>
                    </>
                  )}
                  {isSuperAdmin && (
                    <button
                      onClick={() => setActiveTab('revenue')}
                      className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 ${
                        activeTab === 'revenue'
                          ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      Platform Revenue
                    </button>
                  )}
                </div>
              </div>

              <MetricsGrid metrics={metrics} />

              {/* Tab Content */}
              {activeTab === 'group' && !isSuperAdmin && (
                <div className="mb-8 space-y-6">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6 hover:shadow-strong transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Contribution Trends</h2>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={contributionChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                        <YAxis stroke="var(--color-muted-foreground)" />
                        <Tooltip 
                          formatter={(value: number) => `${value.toLocaleString()} VT`}
                          contentStyle={{
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="var(--color-primary)" 
                          strokeWidth={2}
                          name="Total Amount (VT)"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="count" 
                          stroke="var(--color-accent)" 
                          strokeWidth={2}
                          name="Number of Contributions"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Lending Overview Section */}
                  <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold text-foreground mb-2">Lending Overview</h2>
                      <p className="text-sm text-muted-foreground">
                        Track loans, outstanding balances, and member payment status
                      </p>
                    </div>
                    <LendingOverview />
                  </div>
                </div>
              )}

              {activeTab === 'admin' && (
                <div className="mb-8 space-y-6">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6 hover:shadow-strong transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Login Activity</h2>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={loginChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                        <YAxis stroke="var(--color-muted-foreground)" />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                          }}
                        />
                        <Legend />
                        <Bar dataKey="logins" fill="var(--color-primary)" name="Logins" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
                    <h2 className="text-xl font-semibold text-foreground mb-4">Payment Activity</h2>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={paymentChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                        <YAxis stroke="var(--color-muted-foreground)" />
                        <Tooltip 
                          formatter={(value: number, name: string) => 
                            name === 'amount' ? `${value.toLocaleString()} VT` : value
                          }
                          contentStyle={{
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="payments" 
                          stroke="var(--color-primary)" 
                          strokeWidth={2}
                          name="Number of Payments"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="var(--color-success)" 
                          strokeWidth={2}
                          name="Total Amount (VT)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {activeTab === 'subscription' && (
                <div className="mb-8 space-y-6">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6 hover:shadow-strong transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Subscription Overview</h2>
                    </div>
                    <div className="mb-6">
                      <div className="text-3xl font-bold text-foreground mb-2">
                        {subscriptionData.totalPayments.toLocaleString()} VT
                      </div>
                      <p className="text-muted-foreground">Total Payments Received</p>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={subscriptionData.groupsByPlan}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry: any) => `${entry.plan}: ${entry.count}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {subscriptionData.groupsByPlan.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#EF4444'][index % 4]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {activeTab === 'revenue' && isSuperAdmin && (
                <div className="mb-8 space-y-6">
                  {/* Revenue Overview Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Total Platform Revenue</h3>
                        <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="text-3xl font-bold mb-1">
                        {revenueData.totalRevenue.toLocaleString()} VT
                      </div>
                      <p className="text-blue-100 text-sm">All time revenue</p>
                    </div>

                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">From Invoices</h3>
                        <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="text-3xl font-bold mb-1">
                        {revenueData.totalInvoices.toLocaleString()} VT
                      </div>
                      <p className="text-emerald-100 text-sm">Generated invoices</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">From Subscriptions</h3>
                        <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <div className="text-3xl font-bold mb-1">
                        {revenueData.totalSubscriptions.toLocaleString()} VT
                      </div>
                      <p className="text-purple-100 text-sm">Subscription payments</p>
                    </div>
                  </div>

                  {/* Monthly Revenue Trend */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Monthly Revenue Trend (Last 12 Months)</h2>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={revenueData.monthlyRevenue}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
                        <YAxis stroke="var(--color-muted-foreground)" />
                        <Tooltip 
                          formatter={(value: number) => `${value.toLocaleString()} VT`}
                          contentStyle={{
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                          }}
                        />
                        <Legend />
                        <Bar dataKey="amount" fill="var(--color-primary)" name="Revenue (VT)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Revenue by Subscription Plan */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Revenue by Subscription Plan</h2>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Plan Name</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Total Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Subscriptions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {revenueData.revenueByPlan.map((plan, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.plan}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold">
                                {plan.amount.toLocaleString()} VT
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-600">{plan.count}</td>
                            </tr>
                          ))}
                          {revenueData.revenueByPlan.length === 0 && (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                                No subscription revenue data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Revenue by Group Creator */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Revenue by Group Creator</h2>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Creator Name</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Total Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Groups Created</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {revenueData.revenueByCreator.slice(0, 10).map((creator, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{creator.creatorName}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold">
                                {creator.amount.toLocaleString()} VT
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-600">{creator.groupCount}</td>
                            </tr>
                          ))}
                          {revenueData.revenueByCreator.length === 0 && (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                                No revenue data by creator available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className={isSuperAdmin ? "lg:col-span-3" : "lg:col-span-2"}>
                  <QuickActions actions={quickActions} />
                </div>
                {!isSuperAdmin && (
                  <div className="lg:col-span-1">
                    <UpcomingSchedule upcomingDays={upcomingDays} />
                  </div>
                )}
              </div>

              {/* Show contributions section for all admins (read-only for superadmin) */}
              <div className="mb-6">
                <ContributionsTable
                  contributions={contributions}
                  onApprove={isSuperAdmin ? undefined : handleApprove}
                  onReject={isSuperAdmin ? undefined : handleReject}
                  readOnly={isSuperAdmin}
                  showGroup={isSuperAdmin} />
              </div>

              {/* Super admin info message */}
              {isSuperAdmin && (
                <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 shadow-soft">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-blue-900 mb-2">
                        Super Admin Access
                      </h3>
                      <p className="text-sm text-blue-800 leading-relaxed">
                        As a super admin, you can view all contributions from all groups for oversight. Contribution recording and verification is handled by group administrators.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <SubscriptionManagement />
              </div>

              <div className="mb-6">
                <PaymentApproval />
              </div>

              {isSuperAdmin && (
                <>
                  <div className="mb-6">
                    <InvoiceGeneration />
                  </div>
                  <div className="mb-6">
                    <MemberChargesList showGroupName={true} />
                  </div>
                </>
              )}

              {/* Group Admins Section */}
              <div className="mb-8 bg-white rounded-2xl border border-gray-100 shadow-soft hover:shadow-strong transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Group Administrators</h2>
                      <p className="text-sm text-gray-600">
                        Overview of all group administrators and the number of members they manage
                      </p>
                    </div>
                  </div>
                  
                  {groupAdmins.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No group administrators found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Group(s)</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Members Managed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupAdmins.map((admin) => (
                            <tr key={admin.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                              <td className="py-3 px-4 text-sm text-foreground font-medium">{admin.name}</td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">{admin.email}</td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">{admin.groupName}</td>
                              <td className="py-3 px-4 text-sm text-foreground font-semibold text-right">
                                {admin.memberCount}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Real-time sync status indicator */}
              {!isSuperAdmin && (
                <div className="mb-6">
                  <SyncStatusIndicator />
                </div>
              )}

              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex fixed bottom-6 left-6 w-12 h-12 items-center justify-center bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-full shadow-strong hover:shadow-xl transition-all duration-300 hover:scale-110 z-50"
                aria-label="Toggle sidebar">
                <span className="text-lg font-bold">
                  {isSidebarCollapsed ? '→' : '←'}
                </span>
              </button>
            </div>
          </main>
        </div>
      </div>
    </>);

};

export default AdminDashboard;